import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { travelCariContactSchema } from '@polatkan/api/travel-cari';
import { Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { accountErrorKey } from '@/components/travel/cari/account-form-model';

export function PartnerContactModal({
  open,
  onOpenChange,
  partnerId,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation('travelCari');
  const { t: common } = useTranslation('common');
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<
    z.input<typeof travelCariContactSchema>,
    unknown,
    z.output<typeof travelCariContactSchema>
  >({
    resolver: zodResolver(travelCariContactSchema),
    mode: 'onTouched',
    defaultValues: { name: '', email: '', phone: '', role: '', isPrimary: false },
  });
  const mutation = trpc.partner.addContact.useMutation();
  const pending = isSubmitting || mutation.isPending;
  const utils = trpc.useUtils();
  const submit = async (values: z.output<typeof travelCariContactSchema>) => {
    setError(null);
    try {
      await mutation.mutateAsync({ partnerId, ...values });
    } catch (error) {
      setError(t(`account.errors.${accountErrorKey(error)}`));
      return;
    }
    void utils.travelCari.invalidate();
    void utils.partner.invalidate();
    onOpenChange(false);
    onSuccess?.();
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!pending) onOpenChange(value);
      }}
    >
      <DialogContent closeDisabled={pending} className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('account.addContact')}</DialogTitle>
          <DialogDescription>{t('account.contactHint')}</DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={handleSubmit(submit)} className="space-y-4">
          <fieldset disabled={pending} className="space-y-4">
            {(['name', 'role', 'email', 'phone'] as const).map((field) => (
              <div key={field} className="space-y-1.5">
                <Label htmlFor={`contact-${field}`}>
                  {t(
                    field === 'name'
                      ? 'account.fields.contactName'
                      : field === 'role'
                        ? 'account.contactRole'
                        : `account.fields.${field}`
                  )}
                  {field === 'name' && ' *'}
                </Label>
                <Input
                  id={`contact-${field}`}
                  type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                  {...register(field)}
                  aria-invalid={!!errors[field]}
                  aria-describedby={errors[field] ? `contact-${field}-error` : undefined}
                />
                {errors[field] && (
                  <p id={`contact-${field}-error`} className="text-destructive text-xs">
                    {t(`account.validation.${errors[field]?.message}`, {
                      defaultValue: t('account.validation.review'),
                    })}
                  </p>
                )}
              </div>
            ))}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register('isPrimary')} />
              {t('account.primaryContact')}
            </label>
          </fieldset>
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => onOpenChange(false)}
            >
              {common('cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t(pending ? 'account.saving' : 'account.addContact')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
